/**
 * jQuery の SlideToggle の代わりになるいいライブラリがなかったので、自作しました。問題ありましたらご連絡ください。
 * 開発環境で open = false を指定すると、一瞬開いた状態になりますが、React.Strict の影響だと思われます。本番環境ではそうならないはずです。
 */
import { ReactNode, useEffect, useRef, useState } from 'react';
import { SLIDE_TOGGLE_ANIMATION_DURATION } from '../../constants/animation';

interface Props {
  open: boolean;
  children: ReactNode;
}

/**
 * 子要素をアニメーションで開けたり閉じたりするためのコンポーネント
 * @param
 * @returns
 */
function SlideToggle({ open, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const refHeight = useRef<number>(); // はじめや閉じる前に高さを取得して、保存しておく
  const [currentHeight, setCurrentHeight] = useState<number | 'auto'>('auto'); // 最初は開いておいて、高さを取得する
  const refIsFirst = useRef<boolean>(true); // 最初のときは特殊な動作をするので、最初かどうかを保存しておく
  const [displayNone, setDisplayNone] = useState(false); // 非表示

  useEffect(() => {
    if (open) {
      // 開かれたときは高さを設定し、開き終わったら高さを auto に設定する
      setDisplayNone(false);
      setTimeout(() => {
        if (refHeight.current != null) {
          setCurrentHeight(refHeight.current);
          setTimeout(() => {
            setCurrentHeight('auto');
            setDisplayNone(false);
          }, SLIDE_TOGGLE_ANIMATION_DURATION);
        }
      }, 100);
    } else if (refIsFirst.current) {
      // 【最初に閉じている場合に以下が実行】
      // 閉じた状態から始まる場合は、一瞬開いて高さを取得して、その後閉じる
      if (ref.current != null) {
        refHeight.current = ref.current.scrollHeight;
      }
      setCurrentHeight(0);
    } else {
      // 【最初以外の閉じている場合に以下が実行】
      // 現在の高さを取得して保存し、その高さを要素の height に設定する
      // その後高さを 0 にしてアニメーションさせる
      // 高さを auto → 0 としてもアニメーションは動かない
      if (ref.current != null) {
        refHeight.current = ref.current.scrollHeight;
        setCurrentHeight(ref.current.scrollHeight);
      }
      setTimeout(() => {
        setCurrentHeight(0);
        setTimeout(() => {
          setCurrentHeight(0);
          setDisplayNone(true);
        }, SLIDE_TOGGLE_ANIMATION_DURATION);
      }, 100);
    }
    refIsFirst.current = false;
  }, [open]);

  return (
    <div
      ref={ref}
      style={{
        transition: `height ${SLIDE_TOGGLE_ANIMATION_DURATION}ms`,
        // 開いたときは、visible に設定しないと、子要素のモーダルなど、はみ出して表示されるものが表示されなくなる
        overflow: currentHeight === 'auto' ? 'visible' : 'hidden',
        height: currentHeight,
        opacity: refIsFirst.current && !open ? 0 : 1,
        display: displayNone ? 'none' : undefined,
      }}
    >
      {children}
    </div>
  );
}

export default SlideToggle;
